import React from 'react';
import LazyLoadComponent from '../LazyLoad/LazyLoadComponent';
import { SectionIdProvider } from './SectionIdProvider';
import SectionLoadingSkeleton from './SectionLoadingSkeleton';
import { SectionsBottomToolbar } from './SectionsBottomToolbar';
import { useDeleteSectionHandler } from '../hooks/useDeleteSectionHandler.ts';
import { useEffectiveOrientation } from 'hooks/useEffectiveOrientation';
import { getCanvasMinHeight } from '../shared/gridConfig';
import { useAppSelector } from '../grid/reduxStore/Store';
import { selectContentType } from '../grid/reduxStore/documentSettingsSlice';
import { selectSections } from '../grid/reduxStore/editorSlice';
import { ContentType } from '../../../utils/getContentType.ts';
import { sectionsTestIds } from './index.test-ids';

const Canvas = React.lazy(() => import('./Canvas'));

const EditorContent: React.FC = () => {
  const allSections = useAppSelector(selectSections);
  const deleteSectionHandler = useDeleteSectionHandler();
  const contentType = useAppSelector(selectContentType);
  const isSectionContent = contentType === ContentType.SECTION;
  const deleteSection = deleteSectionHandler;
  const orientation = useEffectiveOrientation();
  const lazyLoadMinHeight = `${getCanvasMinHeight(orientation)}px`;

  if (allSections.length === 0) {
    return <SectionLoadingSkeleton />;
  }

  const sectionsWithRef = allSections.map((section, index) => ({
    id: section.id,
    ref: React.createRef<HTMLDivElement>(),
    isLast: index === allSections.length - 1,
  }));

  const enableDelete = sectionsWithRef.length > 1;

  return sectionsWithRef.map((section, index) => (
    <SectionIdProvider key={section.id} sectionId={section.id}>
      <LazyLoadComponent
        sectionId={section.id}
        className={`lazy-loaded-section lazy-loaded-section-${section.id}`}
        minHeight={lazyLoadMinHeight}
        testId={sectionsTestIds.lazyLoadedSection}
      >
        <Canvas
          data-testid={sectionsTestIds.section(section.id)}
          enableDelete={enableDelete}
          key={section.id}
          deleteButtonOnClick={() => deleteSection(section.id)}
          gridRef={section.ref}
          sectionOrder={index}
        />
        {section.isLast && !isSectionContent && <SectionsBottomToolbar sectionId={section.id} />}
      </LazyLoadComponent>
    </SectionIdProvider>
  ));
};

export default EditorContent;
